import type { LeaveRequest } from "@spork/s4p-server-sdk";
import { Link, type Href } from "expo-router";
import { useTranslation } from "react-i18next";
import { Pressable, View } from "react-native";

import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { formatLeaveRequestDateRange } from "@/lib/date";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/testIdDefinition";

import { LeaveRequestCollapsedActions } from "./LeaveRequestCollapsedActions";

type LeaveRequestListItemProps = {
    request: LeaveRequest;
    childLabel?: string;
    isLast?: boolean;
};

/** Collapsed accordion header: date + secondary status + captions. */
export function LeaveRequestSummary({
    request,
    childLabel,
}: {
    request: LeaveRequest;
    childLabel?: string;
}) {
    const { t } = useTranslation();

    return (
        <View className="min-w-0 flex-1 shrink flex-col">
            <View className="flex-row items-start gap-2">
                <View className="min-w-0 flex-1 shrink">
                    <Text
                        className="min-w-0 flex-1 shrink"
                        testID={
                            TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM_DATE_RANGE]
                        }
                    >
                        {formatLeaveRequestDateRange(
                            request.date_from,
                            request.date_to,
                            t("leave.ongoing"),
                            {
                                timeFrom: request.time_from,
                                timeTo: request.time_to,
                                fromTimeLabel: (time) =>
                                    t("leave.fromTime", { time }),
                                untilTimeLabel: (time) =>
                                    t("leave.untilTime", { time }),
                                leavesAtLabel: (time) =>
                                    t("leave.leavesAtTime", { time }),
                                arrivesAtLabel: (time) =>
                                    t("leave.arrivesAtTime", { time }),
                                weeklyLabel: (days) =>
                                    t("leave.weeklyOn", { days }),
                                dailyLabel: t("ui.daily"),
                                daysOfWeek: request.days_of_week,
                                weekdayLabel: (day) =>
                                    t(`ui.weekdayShort.${day}`),
                            },
                        )}
                    </Text>
                </View>
                <Badge variant="secondary" className="shrink-0">
                    <Text
                        testID={TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM_STATUS]}
                    >
                        {t(`leave.status.${request.status}`)}
                    </Text>
                </Badge>
            </View>
            {childLabel ? (
                <Text
                    variant="caption"
                    testID={TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM_CHILD]}
                >
                    {childLabel}
                </Text>
            ) : null}
            <Text
                variant="caption"
                testID={TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM_REASON]}
            >
                {t(`leave.reasons.${request.reason}`)}
            </Text>
        </View>
    );
}

export function LeaveRequestListItem({
    request,
    childLabel,
    isLast = false,
}: LeaveRequestListItemProps) {
    const { t } = useTranslation();
    const reason = t(`leave.reasons.${request.reason}`);
    const status = t(`leave.status.${request.status}`);
    const dates = formatLeaveRequestDateRange(
        request.date_from,
        request.date_to,
        t("leave.ongoing"),
        {
            timeFrom: request.time_from,
            timeTo: request.time_to,
            fromTimeLabel: (time) => t("leave.fromTime", { time }),
            untilTimeLabel: (time) => t("leave.untilTime", { time }),
            leavesAtLabel: (time) => t("leave.leavesAtTime", { time }),
            arrivesAtLabel: (time) => t("leave.arrivesAtTime", { time }),
            weeklyLabel: (days) => t("leave.weeklyOn", { days }),
            dailyLabel: t("ui.daily"),
            daysOfWeek: request.days_of_week,
            weekdayLabel: (day) => t(`ui.weekdayShort.${day}`),
        },
    );
    const accessibilityLabel = childLabel
        ? t("leave.viewRequestA11yWithChild", {
              name: childLabel,
              reason,
              status,
              dates,
          })
        : t("leave.viewRequestA11y", { reason, status, dates });

    return (
        <View
            testID={TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM]}
            className={cn("bg-card", isLast && "border-b-0")}
        >
            <Link href={`/leave-request/${request.id}` as Href} asChild>
                <Pressable
                    testID={TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM_OPEN_BUTTON]}
                    accessibilityRole="button"
                    accessibilityLabel={accessibilityLabel}
                    className="flex-row items-start justify-between gap-4 rounded-md py-4"
                >
                    <LeaveRequestSummary
                        request={request}
                        childLabel={childLabel}
                    />
                    <Icon
                        name="chevron-right"
                        size={16}
                        className="text-muted-foreground shrink-0"
                    />
                </Pressable>
            </Link>
            <LeaveRequestCollapsedActions request={request} />
        </View>
    );
}
